import { put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import {
  changeStatusSimilarities,
  exportSimilarities,
  exportSimilaritiesById,
  fetchSimilarities,
  fetchSimilaritiesById,
} from 'APIPackage/SimilarityModule/services/similaritiesApi';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ISimilaritiesResponse } from 'APIPackage/SimilarityModule/interfaces/ISimilaritiesResponse';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { ISimilarityMatch } from 'APIPackage/SimilarityModule/interfaces/ISimilarityMatch';
import { fetchSettingByCode } from 'APIPackage/AdminModule/services/adminCatalogsExportsApi';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import {
  similaritiesChangeStatus,
  similaritiesExportById,
  similaritiesExportList,
  similaritiesGetData,
  similaritiesGetDataById,
  similaritiesGetFetchSettingByCode,
  similaritiesSetData,
  similaritiesSetDataById,
  similaritiesSetFetchSettingByCode,
  similaritiesSetFilters,
} from '../actions/similaritiesActions';
import { SimilaritiesActionsEnum } from '../enums/SimilaritiesActionsEnum';
import {
  getSimilaritiesFilter,
  getSimilaritiesFilterById,
  getSimilaritiesOrder,
  getSimilaritiesOrderById,
  getSimilaritiesPage,
  getSimilaritiesPersonId,
  getSimilaritiesSearchString,
} from '../selectors/similaritiesSelectors';

export function* watchSimilaritiesSaga() {
  yield takeEvery(SimilaritiesActionsEnum.SIMILARITIES_INIT_PAGE, similaritiesInitPageSaga);
  yield takeEvery(SimilaritiesActionsEnum.SIMILARITIES_GET_DATA, similaritiesGetDataSaga);
  yield takeEvery(SimilaritiesActionsEnum.SIMILARITIES_GET_DATA_NEXT_PAGE, similaritiesGetDataNextPageSaga);
  yield takeEvery(SimilaritiesActionsEnum.SIMILARITIES_CHANGE_STATUS, similaritiesChangeStatusSaga);
  yield takeEvery(SimilaritiesActionsEnum.SIMILARITIES_EXPORT_LIST, similaritiesExportListSaga);
  yield takeEvery(SimilaritiesActionsEnum.SIMILARITIES_GET_DATA_BY_ID, similaritiesGetDataByIdSaga);
  yield takeEvery(SimilaritiesActionsEnum.SIMILARITIES_EXPORT_BY_ID, similaritiesExportByIdSaga);
  yield takeEvery(
    SimilaritiesActionsEnum.SIMILARITIES_GET_FETCH_SETTING_BY_CODE,
    similaritiesGetFetchSettingByCodeSaga,
  );
}

function* similaritiesInitPageSaga() {
  const { ERP, ERKNM } = KnmVersionsEnum;
  const state: IRootState = yield select();
  const lawInterface = getLawInterface(state);
  const version = lawInterface === LawInterfacesEnum.FZ_248 ? ERKNM : ERP;

  yield put(similaritiesSetFilters({ version }));
  yield put(similaritiesGetData());
}

function* similaritiesGetDataSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const filters = getSimilaritiesFilter(state);
    const order = getSimilaritiesOrder(state);
    const searchString = getSimilaritiesSearchString(state);
    const response: ISimilaritiesResponse = yield fetchSimilarities(
      searchString ? { ...filters, searchString } : filters,
      0,
      order,
    );

    yield put(
      similaritiesSetData(
        response.list,
        response.page,
        response.pageCount,
        response.totalCount,
        response.notProcessedCount,
      ),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* similaritiesGetDataNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const filters = getSimilaritiesFilter(state);
    const order = getSimilaritiesOrder(state);
    const page = getSimilaritiesPage(state);
    const searchString = getSimilaritiesSearchString(state);
    const response: ISimilaritiesResponse = yield fetchSimilarities(
      searchString ? { ...filters, searchString } : filters,
      page + 1,
      order,
    );

    yield put(
      similaritiesSetData(
        response.list,
        response.page,
        response.pageCount,
        response.totalCount,
        response.notProcessedCount,
      ),
    );
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* similaritiesChangeStatusSaga(action: ActionType<typeof similaritiesChangeStatus>) {
  function* funcTry() {
    const { status, id, startDate, filter, isId } = action.payload;

    yield changeStatusSimilarities(status, id, startDate, filter);
    if (isId) {
      similaritiesGetDataById();
    } else {
      yield put(similaritiesGetData());
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* similaritiesExportListSaga(action: ActionType<typeof similaritiesExportList>) {
  function* funcTry() {
    const { format, filters, exportNotify } = action.payload;
    const response: IExport = yield exportSimilarities(format, filters);

    yield exportNotify(response);
    yield put(similaritiesGetData());
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* similaritiesGetDataByIdSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const personId = getSimilaritiesPersonId(state);
    const filters = getSimilaritiesFilterById(state);
    const order = getSimilaritiesOrderById(state);

    if (personId) {
      const response: ISimilarityMatch[] = yield fetchSimilaritiesById(personId, order, filters);

      yield put(similaritiesSetDataById(response));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* similaritiesExportByIdSaga(action: ActionType<typeof similaritiesExportById>) {
  function* funcTry() {
    const { format, personId, filters, exportNotify } = action.payload;
    const response: IExport = yield exportSimilaritiesById(format, personId, filters);

    yield exportNotify(response);
    yield put(similaritiesGetData());
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* similaritiesGetFetchSettingByCodeSaga(action: ActionType<typeof similaritiesGetFetchSettingByCode>) {
  function* funcTry() {
    const { code } = action.payload;

    const response: IAdminSetting = yield fetchSettingByCode(code);

    yield put(similaritiesSetFetchSettingByCode(response));
  }

  yield sagaWrapper({
    funcTry,
  });
}
